import { RotateCcwIcon, SparklesIcon } from 'lucide-react';
import { CompactStepper } from '~/components/atoms/CompactStepper';
import { IconButton } from '~/components/atoms/IconButton';
import { Button } from '~/components/ui/button';
import { Switch } from '~/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { runAutoDetect } from '~/features/grid/actions';
import { uniformDividers } from '~/features/grid/autodetect';
import { PinControls } from '~/features/pin/PinControls';
import { useStore } from '~/store';
import type { FramingMode } from '~/types/pipeline';
import { Divider, GroupLabel, MorePopover } from '../toolbar/parts';

export function FramingPanel() {
  const framingMode = useStore((s) => s.pin.framingMode);
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border px-1 py-0.5">
      <ModeToggle />
      <Divider />
      {framingMode === 'pin' ? <PinControls showLabel={false} /> : <UniformControls />}
    </div>
  );
}

function ModeToggle() {
  const mode = useStore((s) => s.pin.framingMode);
  const setMode = useStore((s) => s.setFramingMode);

  const btn = (value: FramingMode, label: string, tooltip: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="xs"
          variant={mode === value ? 'default' : 'outline'}
          aria-pressed={mode === value}
          onClick={() => setMode(value)}
          className="h-7"
        >
          {label}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex items-center gap-1.5">
      <GroupLabel>Framing</GroupLabel>
      <div className="flex gap-0.5">
        {btn('uniform', 'Uniform', 'Uniform N×M grid — pick when frames are evenly spaced.')}
        {btn('pin', 'Pin', 'Auto-detect frames from foreground.')}
      </div>
    </div>
  );
}

function UniformControls() {
  const cropRect = useStore((s) => s.grid.cropRect);
  const cols = useStore((s) => s.grid.colDividers);
  const rows = useStore((s) => s.grid.rowDividers);
  const committed = useStore((s) => s.workflow.committedCleaned);
  const setColDividers = useStore((s) => s.setColDividers);
  const setRowDividers = useStore((s) => s.setRowDividers);
  const clearDividers = useStore((s) => s.clearDividers);

  const colCount = cols ? cols.length - 1 : null;
  const rowCount = rows ? rows.length - 1 : null;

  const setColCount = (n: number) => {
    if (!cropRect) return;
    setColDividers(uniformDividers(cropRect.x, cropRect.w, n));
  };
  const setRowCount = (n: number) => {
    if (!cropRect) return;
    setRowDividers(uniformDividers(cropRect.y, cropRect.h, n));
  };

  return (
    <div className="flex items-center gap-1.5">
      <CompactStepper
        ariaLabel="Columns"
        value={colCount}
        onChange={setColCount}
        disabled={!cropRect}
      />
      <span className="text-muted-foreground/70">×</span>
      <CompactStepper
        ariaLabel="Rows"
        value={rowCount}
        onChange={setRowCount}
        disabled={!cropRect}
      />
      <IconButton
        label="Auto-detect grid"
        onClick={runAutoDetect}
        disabled={!cropRect || !committed}
      >
        <SparklesIcon />
      </IconButton>
      <IconButton label="Reset rows & columns" onClick={clearDividers} disabled={!cols && !rows}>
        <RotateCcwIcon />
      </IconButton>
      <MorePopover label="Grid">
        <GridMore />
      </MorePopover>
    </div>
  );
}

function GridMore() {
  const indivCols = useStore((s) => s.grid.individualCols);
  const indivRows = useStore((s) => s.grid.individualRows);
  const autoConfidence = useStore((s) => s.grid.autoConfidence);
  const setIndivCols = useStore((s) => s.setIndividualCols);
  const setIndivRows = useStore((s) => s.setIndividualRows);

  return (
    <>
      <div className="flex items-center justify-between text-xs">
        <span>Drag columns independently</span>
        <Switch checked={indivCols} onCheckedChange={setIndivCols} />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span>Drag rows independently</span>
        <Switch checked={indivRows} onCheckedChange={setIndivRows} />
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Off: dragging a divider reflows the grid uniformly. On: each divider moves on its own.
      </p>
      {autoConfidence != null && (
        <p className="text-[11px] text-muted-foreground">
          Auto-detect confidence: {(autoConfidence * 100).toFixed(0)}%
        </p>
      )}
    </>
  );
}
